import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Truck, Globe, Leaf } from "@phosphor-icons/react";
import { useLang } from "../i18n/LanguageContext";
import SEO from "../components/SEO";

const About = () => {
  const { t } = useLang();
  const values = [
    { icon: Truck, title: t.about.v1, body: t.about.v1d },
    { icon: ShieldCheck, title: t.about.v2, body: t.about.v2d },
    { icon: Globe, title: t.about.v3, body: t.about.v3d },
    { icon: Leaf, title: t.about.v4, body: t.about.v4d },
  ];

  return (
    <>
      <SEO
        title="About Masika Chem SARL"
        description="Masika Chem SARL — a Lubumbashi-based partner supplying world-class mining operations across the Greater Katanga region with reliable industrial chemistry."
        path="/about"
      />

      {/* HERO */}
      <section className="relative bg-neutral-900 text-white" data-testid="about-hero">
        <div className="grid grid-cols-1 md:grid-cols-12 min-h-[60vh]">
          <div className="md:col-span-7 flex items-center px-5 lg:px-16 py-20">
            <div>
              <div className="inline-flex items-center gap-3 mb-6">
                <span className="w-10 h-px bg-[#E85A1F]" />
                <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-[#E85A1F]">
                  {t.about.overline}
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
                {t.about.title}
              </h1>
              <p className="mt-7 text-lg text-neutral-300 max-w-xl">{t.about.sub}</p>
            </div>
          </div>
          <div className="md:col-span-5 relative min-h-[40vh]">
            <img
              src="https://images.unsplash.com/photo-1672748341520-6a839e6c05bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwd29ya2VyJTIwaGVsbWV0fGVufDB8fHx8MTc4MDY4OTIzOXww&ixlib=rb-4.1.0&q=85"
              alt="Industrial worker in red hard hat"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* MISSION + VISION */}
      <section className="bg-white py-24" data-testid="about-mission-vision">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 border border-neutral-200">
            <div className="p-10 lg:p-14 border-b md:border-b-0 md:border-r border-neutral-200">
              <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-[#C8102E]">
                01 / {t.about.missionTitle}
              </span>
              <h2 className="mt-5 text-3xl lg:text-4xl font-extrabold text-neutral-900 tracking-tight leading-[1.1]">
                {t.about.missionBody}
              </h2>
            </div>
            <div className="p-10 lg:p-14">
              <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-[#E85A1F]">
                02 / {t.about.visionTitle}
              </span>
              <h2 className="mt-5 text-3xl lg:text-4xl font-extrabold text-neutral-900 tracking-tight leading-[1.1]">
                {t.about.visionBody}
              </h2>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-[#F7F7F5] py-24" data-testid="about-values">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <div className="mb-14 max-w-2xl">
            <div className="inline-flex items-center gap-3 mb-4">
              <span className="w-10 h-px bg-[#C8102E]" />
              <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-[#C8102E]">
                {t.about.valuesTitle}
              </span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-extrabold text-neutral-900 leading-[1.05] tracking-tight">
              {t.about.valuesTitle}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-neutral-200 border border-neutral-200">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={i} className="bg-white p-8 lg:p-10" data-testid={`about-value-${i}`}>
                  <Icon size={28} weight="duotone" className="text-[#C8102E] mb-6" />
                  <h3 className="text-xl font-bold text-neutral-900 mb-3">{v.title}</h3>
                  <p className="text-sm text-neutral-600 leading-relaxed">{v.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-neutral-900 text-white py-20" data-testid="about-cta">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
          <h3 className="text-3xl lg:text-4xl font-extrabold max-w-2xl leading-[1.1] tracking-tight">
            Discover the full Masika Chem product catalog.
          </h3>
          <Link
            to="/products"
            data-testid="about-cta-products"
            className="inline-flex items-center gap-2 bg-[#C8102E] hover:bg-[#A00D25] text-white px-7 py-4 font-semibold"
          >
            {t.common.exploreProducts} <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default About;
